import styles from "./Split.module.scss";

export type Breakpoints =
  | "mobile"
  | "tablet"
  | "desktop"
  | "widescreen"
  | "fullhd"
  | "ultrahd";

const breakpoints: Breakpoints[] = [
  "mobile",
  "tablet",
  "desktop",
  "widescreen",
  "fullhd",
  "ultrahd",
];
const EMPTY_LEN = "0px";
const EMPTY_POS = "static";
const EMPTY_MAX = "none";

export interface SplitProps {
  first: React.ReactElement;
  firstClassName?: string;
  firstOuterFractional?: React.ReactElement;
  second: React.ReactElement;
  secondClassName?: string;
  secondOuterFractional?: React.ReactElement;
  direction: "row-reverse" | "row";
  verticalBreapoints?:
    | Breakpoints
    | { [key in Breakpoints]?: "horizontal" | "vertical" };
  firstWidth?: string | { [key in Breakpoints]?: string };
  secondWidth?: string | { [key in Breakpoints]?: string };
  firstVerticalAlignment?: "start" | "center" | "end" | "stretch";
  secondVerticalAlignment?: "start" | "center" | "end" | "stretch";
  gap?: string | { [key in Breakpoints]?: string };
  verticalGap?: string | { [key in Breakpoints]?: string };
  gapOn?: "both" | "first" | "second";
  containerWidthLimit?: {
    first: boolean;
    second: boolean;
  };
  sticky?: "first" | "second";
  stickyMaxHeight?: string;
  stickyTop?: string;
  extraParentClasses?: string;
  extraProps?: React.HTMLAttributes<HTMLDivElement>;
  verticalDirection?: "column" | "column-reverse";
}

const Split = ({
  first,
  firstClassName,
  firstOuterFractional,
  second,
  secondClassName,
  secondOuterFractional,
  direction = "row",
  verticalBreapoints = "mobile",
  firstWidth = "50%",
  secondWidth = "50%",
  firstVerticalAlignment,
  secondVerticalAlignment,
  containerWidthLimit,
  sticky,
  stickyMaxHeight = "100vh",
  stickyTop,
  gap,
  verticalGap,
  gapOn,
  extraProps,
  extraParentClasses,
  verticalDirection = "column",
}: SplitProps) => {
  let directionBreakpoints = {
    ["--direction-mobile"]: verticalDirection,
  };
  let verticalFractionalPaddingX = {};
  let firstSticky = {};
  let secondSticky = {};
  let firstWidthBreakpoints = {};
  let secondWidthBreakpoints = {};
  let gapBreakpoints = {};
  let verticalGapBreakpoints = {};

  let last: "column" | "column-reverse" | "row-reverse" | "row" =
    verticalDirection;
  let lastFirstWidth = typeof firstWidth === "string" ? firstWidth : "50%";
  let lastSecondWidth = typeof secondWidth === "string" ? secondWidth : "50%";

  let lastGap = typeof gap === "string" ? gap : "";
  let lastVerticalGap = typeof verticalGap === "string" ? verticalGap : "";

  for (const breakpoint of breakpoints) {
    let currentDirection;
    let currentFirstWidth;
    let currentSecondWidth;

    if (
      typeof verticalBreapoints === "string" &&
      verticalBreapoints === breakpoint
    ) {
      //if verticalBreakpoint is only supplied as string -> we found the last breakpoint before switch to horizontal
      currentDirection = verticalDirection;
      last = direction; //set last to the horizontal direction so that for the next breakpoints from now on we take it (because no of the if statements is met and we fall back to last)
    }
    if (
      typeof verticalBreapoints !== "string" &&
      verticalBreapoints[breakpoint]
    ) {
      currentDirection =
        verticalBreapoints[breakpoint] === "horizontal"
          ? direction
          : verticalDirection;
      last = currentDirection;
    }

    directionBreakpoints = {
      ...directionBreakpoints,
      [`--direction-${breakpoint}`]: currentDirection ?? last,
    };

    const isVertical =
      (currentDirection ?? last) === "column" ||
      (currentDirection ?? last) === "column-reverse";
    verticalFractionalPaddingX = {
      ...verticalFractionalPaddingX,
      [`--vertical-fractional-padding-x-${breakpoint}`]: isVertical
        ? "var(--section-padding-x)"
        : EMPTY_LEN,
    };

    firstSticky = {
      ...firstSticky,
      [`--first-sticky-${breakpoint}`]:
        !isVertical && sticky === "first" ? "sticky" : EMPTY_POS,
    };
    secondSticky = {
      ...secondSticky,
      [`--second-sticky-${breakpoint}`]:
        !isVertical && sticky === "second" ? "sticky" : EMPTY_POS,
    };

    firstSticky = {
      ...firstSticky,
      [`--first-sticky-max-height${breakpoint}`]:
        !isVertical && sticky === "first" ? stickyMaxHeight : EMPTY_MAX,
    };
    secondSticky = {
      ...secondSticky,
      [`--second-sticky-max-height${breakpoint}`]:
        !isVertical && sticky === "second" ? stickyMaxHeight : EMPTY_MAX,
    };

    if (typeof firstWidth !== "string" && firstWidth[breakpoint]) {
      currentFirstWidth = firstWidth[breakpoint];
      lastFirstWidth = currentFirstWidth;
    }
    if (typeof secondWidth !== "string" && secondWidth[breakpoint]) {
      currentSecondWidth = secondWidth[breakpoint];
      lastSecondWidth = currentSecondWidth;
    }

    if (gap && typeof gap !== "string" && gap[breakpoint]) {
      lastGap = gap[breakpoint];
    }
    if (
      verticalGap &&
      typeof verticalGap !== "string" &&
      verticalGap[breakpoint]
    ) {
      lastVerticalGap = verticalGap[breakpoint];
    }

    firstWidthBreakpoints = {
      ...firstWidthBreakpoints,
      [`--width-first-${breakpoint}`]:
        (currentDirection ?? last) === "column" ||
        (currentDirection ?? last) === "column-reverse"
          ? "100%"
          : (currentFirstWidth ?? lastFirstWidth),
    };

    secondWidthBreakpoints = {
      ...secondWidthBreakpoints,
      [`--width-second-${breakpoint}`]:
        (currentDirection ?? last) === "column" ||
        (currentDirection ?? last) === "column-reverse"
          ? "100%"
          : (currentSecondWidth ?? lastSecondWidth),
    };

    // firstWidthBreakpoints = {
    //   ...firstWidthBreakpoints,
    //   [`--width-first-${breakpoint}`]:
    //     (currentDirection ?? verticalDirection)
    //       ? "100%"
    //       : (currentFirstWidth ?? lastFirstWidth),
    // };
    // secondWidthBreakpoints = {
    //   ...secondWidthBreakpoints,
    //   [`--width-second-${breakpoint}`]:
    //     (currentDirection ?? verticalDirection)
    //       ? "100%"
    //       : (currentSecondWidth ?? lastSecondWidth),
    // };

    gapBreakpoints = {
      ...gapBreakpoints,
      [`--gap-${breakpoint}`]: isVertical ? EMPTY_LEN : lastGap || EMPTY_LEN,
    };
    verticalGapBreakpoints = {
      ...verticalGapBreakpoints,
      [`--vertical-gap-${breakpoint}`]: isVertical
        ? lastVerticalGap || EMPTY_LEN
        : EMPTY_LEN,
    };
  }

  const firstVerticalAlignmentClass = firstVerticalAlignment
    ? "is-flex is-flex-direction-column is-justify-content-" +
      firstVerticalAlignment
    : "";
  const secondVerticalAlignmentClass = secondVerticalAlignment
    ? "is-flex is-flex-direction-column is-justify-content-" +
      secondVerticalAlignment
    : "";

  // const justifyContentClass = `${containerWidthLimit?.first && containerWidthLimit?.second ? "is-justify-content-center" : containerWidthLimit?.first ? "is-justify-content-flex-end" : containerWidthLimit?.second ? "is-justify-content-flex-start" : ""}`;

  return (
    <div
      {...extraProps}
      className={`${styles["split"]} ${extraParentClasses || ""} ${styles[direction]} ${gapOn ? styles["gap-on-" + gapOn] : ""}`}
      style={
        {
          ...extraProps?.style,
          ...directionBreakpoints,
          ...verticalFractionalPaddingX,
          ...firstSticky,
          ...secondSticky,
          ...firstWidthBreakpoints,
          ...secondWidthBreakpoints,
          ...gapBreakpoints,
          ...verticalGapBreakpoints,
          "--sticky-top": stickyTop ?? EMPTY_LEN,
        } as React.CSSProperties
      }
    >
      <div
        className={`${styles.first} ${firstVerticalAlignment === "center" ? styles["vertical-center"] : ""}`}
      >
        <div className={`${styles["outer-fractional"]}`}>
          {firstOuterFractional}
        </div>
        <div
          className={`${styles.content} ${firstClassName} ${firstVerticalAlignmentClass} ${containerWidthLimit?.first ? `is-fractional-container ${styles["fractional-container"]}` : ""}`}
        >
          {first}
        </div>
        <div className={`${styles["outer-bottom"]}`}></div>
      </div>
      <div
        className={`${styles.second} ${secondVerticalAlignment === "center" ? styles["vertical-center"] : ""}`}
      >
        <div className={`${styles["outer-fractional"]}`}>
          {secondOuterFractional}
        </div>
        <div
          className={`${styles.content} ${secondClassName} ${secondVerticalAlignmentClass} ${containerWidthLimit?.second ? `is-fractional-container ${styles["fractional-container"]}` : ""}`}
        >
          {second}
        </div>
        <div className={`${styles["outer-bottom"]}`}></div>
      </div>
    </div>
  );
};

export default Split;
